import React, { useContext, useEffect, useMemo, useState } from "react"

import {
  getFileExtension,
  getFileNameWithoutExtension
} from "shared/lib/file/getFileExtension"
import { generateUUID } from "shared/lib/generateUUID"
import { noop } from "shared/lib/noop"
import { useEvent } from "shared/lib/useEvent"
import { AttachmentType } from "shared/types/__generated__/graphql"

import { FileUploadPropsContext } from "./FileUploadPropsContext"

export interface UploadingFile {
  id: string
  file: File
  name: string
  extension: string
  preview: string
  status: "uploading" | "success" | "error"
}

export interface FileUploadStateContextType {
  uploadingFiles: UploadingFile[]
  frozenFiles: AttachmentType[] | null | undefined
  addFiles: (files: File[]) => void
  clearCompletedUploads: () => void
}

export const FileUploadStateContext =
  React.createContext<FileUploadStateContextType>({
    uploadingFiles: [],
    frozenFiles: undefined,
    addFiles: noop,
    clearCompletedUploads: noop
  })

interface FileUploadStateProviderProps {
  children: React.ReactNode
}

export function FileUploadStateProvider({
  children
}: FileUploadStateProviderProps) {
  const { onUpload, onUploadComplete, files } = useContext(
    FileUploadPropsContext
  )
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  const [frozenFiles, setFrozenFiles] = useState<
    AttachmentType[] | null | undefined
  >(undefined)

  const addFiles = useEvent(async (filesToUpload: File[]) => {
    // Snapshot files if this is the first upload
    if (uploadingFiles.length === 0) {
      // Normalize to array for consistent internal state
      let normalizedFiles: AttachmentType[] | null = null
      if (files) {
        normalizedFiles = Array.isArray(files) ? files : [files]
      }
      setFrozenFiles(normalizedFiles)
    }
    const newFiles: UploadingFile[] = filesToUpload.map(file => ({
      id: generateUUID(),
      file,
      name: getFileNameWithoutExtension(file.name),
      extension: getFileExtension(file.name),
      preview: URL.createObjectURL(file),
      status: "uploading" as const
    }))

    setUploadingFiles(prev => [...prev, ...newFiles])

    // Execute uploads in parallel
    const results = await Promise.all(
      newFiles.map(async uploadFile => {
        try {
          const result = await onUpload?.({
            id: uploadFile.id,
            file: uploadFile.file,
            url: uploadFile.preview
          })
          return { id: uploadFile.id, success: !result?.errors?.length }
        } catch {
          return { id: uploadFile.id, success: false }
        }
      })
    )

    // Update status based on results
    setUploadingFiles(prev =>
      prev.map(file => {
        const result = results.find(r => r.id === file.id)
        if (result) {
          return { ...file, status: result.success ? "success" : "error" }
        }
        return file
      })
    )

    // Call onUploadComplete callback
    const successful = newFiles.filter((_, i) => results[i]?.success === true)
    const failed = newFiles.filter((_, i) => results[i]?.success === false)

    await onUploadComplete?.(
      successful.map(f => ({ id: f.id, file: f.file, url: f.preview })),
      failed.map(f => ({ id: f.id, file: f.file, url: f.preview }))
    )

    // Clear completed uploads after 2 seconds
    setTimeout(() => {
      clearCompletedUploads()
    }, 2000)
  })

  const clearCompletedUploads = useEvent(() => {
    setUploadingFiles(prev => {
      const completed = prev.filter(
        f => f.status === "success" || f.status === "error"
      )
      completed.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      })
      const remaining = prev.filter(f => f.status === "uploading")

      // Clear frozen snapshot when no uploads remain
      if (remaining.length === 0) {
        setFrozenFiles(undefined)
      }

      return remaining
    })
  })

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      uploadingFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      })
    }
  }, [uploadingFiles])

  const value: FileUploadStateContextType = useMemo(
    () => ({
      uploadingFiles,
      frozenFiles,
      addFiles,
      clearCompletedUploads
    }),
    [uploadingFiles, frozenFiles, addFiles, clearCompletedUploads]
  )

  return (
    <FileUploadStateContext.Provider value={value}>
      {children}
    </FileUploadStateContext.Provider>
  )
}
